// ComplianceReadinessMeter — the "🟢🟡🟠🔴 Ready for Audit" status
// widget from the Compliance OS spec (stand-out feature) + the
// #10 Missing Information Detector.
//
// Exports:
//   computeReadiness(client)         — pure scoring function
//   <ReadinessBadge status .../>     — small pill (for lists, tables, modal header)
//   <ReadinessDetailPanel .../>      — full breakdown + resolve links
//   <MissingInfoDetector .../>       — dedicated Missing Info panel
//
// Scoring inputs (all pulled by the calling view — the meter is
// stateless):
//   · audit packet    /api/audit/packet?client_id=X
//   · client_documents count by required doc_type
//   · client_medical_profile fields
//   · health_policies primary row (identity fields)
//
// Buckets:
//   ready       ≥ 95%  🟢 Ready for Audit
//   minor       80-94% 🟡 Minor Issues
//   action      60-79% 🟠 Action Required
//   priority    < 60%  🔴 High Priority

import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import { supabase } from '../lib/supabase';

async function apiFetch(path) {
  let jwt = null;
  jwt = readPersistedJwtDirect();
  const res = await fetch(path, { headers: { ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}) } });
  if (!res.ok) return null;
  return res.json().catch(() => null);
}
async function safe(fn, fb) { try { const r = await fn(); return r ?? fb; } catch { return fb; } }

/* Required identity fields the AI Missing-Info Detector watches. */
const IDENTITY_FIELDS = [
  { key: 'client_name',   label: 'Full name',   source: 'primary' },
  { key: 'client_dob',    label: 'DOB',         source: 'primary' },
  { key: 'client_phone',  label: 'Phone',       source: 'primary' },
  { key: 'client_email',  label: 'Email',       source: 'primary' },
  { key: 'client_address',label: 'Address',     source: 'primary' },
  { key: 'client_county', label: 'County',      source: 'primary' },
];
const CONTEXT_FIELDS = [
  { key: 'annual_income',  label: 'Annual income (ACA)', source: 'income',   optional: true },
  { key: 'primary_doctor', label: 'Primary doctor',       source: 'profile',  optional: true },
  { key: 'current_prescriptions', label: 'Prescriptions', source: 'profile',  optional: true, array: true },
];

/* Buckets — sorted highest-to-lowest so we can find the first
 * matching threshold. */
const LEVELS = [
  { key: 'ready',    min: 95, label: 'Ready for Audit',   dot: 'bg-emerald-500', text: 'text-emerald-800', bg: 'bg-emerald-50', border: 'border-emerald-200', emoji: '🟢' },
  { key: 'minor',    min: 80, label: 'Minor Issues',      dot: 'bg-amber-400',   text: 'text-amber-800',   bg: 'bg-amber-50',   border: 'border-amber-200',   emoji: '🟡' },
  { key: 'action',   min: 60, label: 'Action Required',   dot: 'bg-orange-500',  text: 'text-orange-800',  bg: 'bg-orange-50',  border: 'border-orange-200',  emoji: '🟠' },
  { key: 'priority', min: 0,  label: 'High Priority',     dot: 'bg-rose-500',    text: 'text-rose-700',    bg: 'bg-rose-50',    border: 'border-rose-200',    emoji: '🔴' },
];
export function bucketFor(pct) { return LEVELS.find(l => pct >= l.min) || LEVELS[LEVELS.length - 1]; }

/* Pure scoring. Weighted:
 *   compliance packet (7 checks) — 45%
 *   document checklist   (4)     — 30%
 *   missing identity (6)         — 15%
 *   ancillary context (3)        — 10%   (soft)
 */
export function computeReadiness({ packet, primary, income, profile, docsByType }) {
  const events = packet?.events || [];
  const forms  = packet?.forms  || [];

  const complianceChecks = [
    { key: 'consent',     done: events.some(e => /consent|hipaa|permission/i.test(e.event_type || '')) },
    { key: 'sms',         done: events.some(e => /sms|text/i.test(e.event_type || '')) },
    { key: 'email',       done: events.some(e => /email/i.test(e.event_type || '')) },
    { key: 'marketplace', done: (packet?.checklists || []).some(c => c.items) },
    { key: 'signature',   done: forms.some(f => (f.signatures?.client || f.signatures?.agent)) },
    { key: 'call',        done: events.some(e => /call.*record|recording/i.test(e.event_type || '')) },
    { key: 'docs',        done: events.some(e => /doc|upload/i.test(e.event_type || '')) || forms.some(f => f.certificate_url) },
  ];
  const compDone = complianceChecks.filter(c => c.done).length;
  const compScore = compDone / complianceChecks.length;

  const requiredDocs = ['application', 'proof_of_income', 'drivers_license', 'marketplace_document'];
  const docDone = requiredDocs.filter(t => (docsByType?.[t] || 0) > 0).length;
  const docScore = docDone / requiredDocs.length;

  const missingIdentity = IDENTITY_FIELDS.filter(f => {
    const src = f.source === 'primary' ? primary : f.source === 'profile' ? profile : income;
    const v = src?.[f.key];
    return v == null || v === '' || (Array.isArray(v) && v.length === 0);
  });
  const idScore = 1 - (missingIdentity.length / IDENTITY_FIELDS.length);

  const missingContext = CONTEXT_FIELDS.filter(f => {
    const src = f.source === 'primary' ? primary : f.source === 'profile' ? profile : income;
    const v = src?.[f.key];
    return v == null || v === '' || (f.array && Array.isArray(v) && v.length === 0);
  });
  const ctxScore = 1 - (missingContext.length / CONTEXT_FIELDS.length);

  const pct = Math.round((compScore * 0.45 + docScore * 0.30 + idScore * 0.15 + ctxScore * 0.10) * 100);
  const bucket = bucketFor(pct);

  const blockers = [];
  for (const c of complianceChecks) if (!c.done) blockers.push({ area: 'compliance', key: c.key });
  for (const t of requiredDocs)     if ((docsByType?.[t] || 0) === 0) blockers.push({ area: 'documents', key: t });
  for (const f of missingIdentity)  blockers.push({ area: 'identity', key: f.key, label: f.label });
  for (const f of missingContext)   blockers.push({ area: 'context', key: f.key, label: f.label });

  return { pct, bucket, blockers, missing: { identity: missingIdentity, context: missingContext } };
}

/* Fetch all inputs the meter needs for a single client. */
export async function loadReadinessInputs(clientId, ownerId) {
  const [packet, primary, income, profile, docs] = await Promise.all([
    safe(() => apiFetch(`/api/audit/packet?client_id=${encodeURIComponent(clientId)}`), null),
    safe(async () => {
      const { data } = await supabase.from('health_policies')
        .select('client_name, client_dob, client_phone, client_email, client_address, client_county')
        .eq('owner_id', ownerId).eq('client_id', clientId).limit(1).maybeSingle();
      return data;
    }, null),
    safe(async () => {
      const { data } = await supabase.from('aca_subsidy_snapshots')
        .select('annual_income')
        .eq('client_id', clientId).order('created_at', { ascending: false }).limit(1).maybeSingle();
      return data;
    }, null),
    safe(async () => {
      const { data } = await supabase.from('client_medical_profile')
        .select('primary_doctor, current_prescriptions')
        .eq('owner_id', ownerId).eq('client_id', clientId).maybeSingle();
      return data;
    }, null),
    safe(async () => {
      const { data } = await supabase.from('client_documents')
        .select('doc_type').eq('owner_id', ownerId).eq('client_id', clientId);
      const by = {};
      for (const d of (data || [])) by[d.doc_type] = (by[d.doc_type] || 0) + 1;
      return by;
    }, {}),
  ]);
  return { packet, primary, income, profile, docsByType: docs };
}

/* ─────────────────────────────────────────────────────────────
   Widgets
   ───────────────────────────────────────────────────────────── */

export function ReadinessBadge({ pct, bucket, compact = false }) {
  const b = bucket || bucketFor(pct);
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border ${b.border} ${b.bg} px-2.5 py-0.5`}>
      <span className={`w-2 h-2 rounded-full ${b.dot}`}/>
      <span className={`text-[10.5px] font-bold uppercase tracking-wider ${b.text}`}>
        {compact ? b.label.split(' ')[0] : b.label}
      </span>
      {pct != null && <span className={`text-[10.5px] font-black tabular-nums ${b.text}`}>{pct}%</span>}
    </span>
  );
}

export function ReadinessDetailPanel({ pct, bucket, blockers, onGotoTab }) {
  const b = bucket || bucketFor(pct);
  if (blockers.length === 0) {
    return (
      <div className={`border rounded-2xl p-4 ${b.border} ${b.bg}`}>
        <div className="flex items-center gap-2">
          <span className={`w-2.5 h-2.5 rounded-full ${b.dot}`}/>
          <div className={`text-[13.5px] font-bold ${b.text}`}>{b.label} — nothing to resolve.</div>
        </div>
      </div>
    );
  }
  const byArea = { compliance: [], documents: [], identity: [], context: [] };
  for (const bl of blockers) (byArea[bl.area] || (byArea[bl.area] = [])).push(bl);
  return (
    <div className={`border rounded-2xl p-5 ${b.border} ${b.bg}`}>
      <div className="flex items-baseline justify-between">
        <div className="flex items-center gap-2">
          <span className={`w-2.5 h-2.5 rounded-full ${b.dot}`}/>
          <div className={`text-[13.5px] font-bold ${b.text}`}>{b.label}</div>
        </div>
        <div className={`text-[24px] font-black tabular-nums ${b.text}`}>{pct}%</div>
      </div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        {byArea.compliance.length > 0 && <BlockerList title="Compliance" rows={byArea.compliance.map(b => b.key.replace(/_/g, ' '))} onClick={() => onGotoTab?.('health_consents')}/>}
        {byArea.documents.length > 0 && <BlockerList title="Missing documents" rows={byArea.documents.map(b => b.key.replace(/_/g, ' '))} onClick={() => onGotoTab?.('health_clients')}/>}
        {byArea.identity.length > 0 && <BlockerList title="Missing identity fields" rows={byArea.identity.map(b => b.label)}/>}
        {byArea.context.length > 0 && <BlockerList title="Suggested context" rows={byArea.context.map(b => b.label)}/>}
      </div>
    </div>
  );
}

function BlockerList({ title, rows, onClick }) {
  return (
    <div className="bg-white/60 border border-slate-200 rounded-lg p-3">
      <div className="flex items-baseline justify-between">
        <div className="text-[10.5px] font-bold uppercase tracking-widest text-slate-500">{title}</div>
        {onClick && <button onClick={onClick} className="text-[10.5px] text-slate-500 hover:text-slate-800">Resolve →</button>}
      </div>
      <ul className="mt-2 space-y-0.5">
        {rows.map((r, i) => (
          <li key={i} className="text-[12.5px] text-slate-800 flex items-center gap-2">
            <span className="text-slate-400">•</span>{r}
          </li>
        ))}
      </ul>
    </div>
  );
}

/* Missing Info Detector — same underlying inputs, dedicated
 * presentation so it can be reused inside the modal Overview. */
export function MissingInfoDetector({ clientId, ownerId, onGotoTab }) {
  const [state, setState] = React.useState({ loading: true });
  React.useEffect(() => {
    if (!clientId || !ownerId) return;
    let cancelled = false;
    (async () => {
      const inputs = await loadReadinessInputs(clientId, ownerId);
      if (cancelled) return;
      const readiness = computeReadiness(inputs);
      setState({ loading: false, ...readiness });
    })();
    return () => { cancelled = true; };
  }, [clientId, ownerId]);

  if (state.loading) return null;
  const idMissing = state.missing.identity;
  const ctxMissing = state.missing.context;
  if (idMissing.length === 0 && ctxMissing.length === 0) return null;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5">
      <div className="flex items-baseline justify-between">
        <div className="text-[11.5px] font-bold uppercase tracking-[0.2em] text-slate-700 flex items-center gap-1.5">
          <span className="text-[#e8a020]">✦</span>
          <span>Missing information detector</span>
        </div>
        <ReadinessBadge pct={state.pct} bucket={state.bucket}/>
      </div>
      <div className="text-[11.5px] text-slate-500 mt-1">
        The AI flags fields the compliance packet expects. Missing context isn't a blocker but sharpens the pre-call brief.
      </div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        {idMissing.length > 0 && (
          <div>
            <div className="text-[10.5px] font-bold uppercase tracking-widest text-rose-700 mb-2">
              Required · {idMissing.length}
            </div>
            <ul className="space-y-0.5">
              {idMissing.map(f => (
                <li key={f.key} className="text-[13px] text-slate-800 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-rose-500"/>{f.label}
                </li>
              ))}
            </ul>
          </div>
        )}
        {ctxMissing.length > 0 && (
          <div>
            <div className="text-[10.5px] font-bold uppercase tracking-widest text-slate-500 mb-2">
              Suggested · {ctxMissing.length}
            </div>
            <ul className="space-y-0.5">
              {ctxMissing.map(f => (
                <li key={f.key} className="text-[13px] text-slate-800 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-400"/>{f.label}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
